/**
 * Top-centre banner stack: garrison alert changes, Osprey calls, objective ticks, pickups and
 * casualties. Each banner slides in, holds about 2.5 s and removes itself (CSS animation).
 */

import './banners.css';
import type { BindingsPort } from '../../input/bindings';
import { isRefusal } from '../../sim/damage';
import type { MissionDef, SimEvent, World } from '../../sim/types';
import { clamp, el } from './dom';
import type { Tone } from './dom';
import { chargesFiredText } from './missionCharges';
import { blackoutText, completeText, progressText, timedEventText, uselessChargeText } from './missionRules';
import { signalText } from './missionSignal';
import { keyHint } from './weaponSlots';

/** Oldest is dropped beyond this; four lets an ALARM survive a burst of lesser notices. */
const MAX_BANNERS = 4;

/**
 * A refusal: the sim words it as a pickup ("NO EFFECT - THE DOOR IS ON THE NORTH FACE", src/sim/damage.ts)
 * and it used to ride the amber pickup blip, so telling the player his grenade did nothing looked exactly
 * like telling him he had found three magazines. It gets its own kind - so a second refusal replaces the
 * first instead of stacking - and the red-amber of a warning. The test lives with the words, in the sim.
 */
export { isRefusal };

/** Garrison alert 0-3 (02 §6.3). */
const ALERT_BANNERS: ReadonlyArray<{ text: string; tone: Tone }> = [
  { text: 'ALL QUIET', tone: 'green' },
  { text: 'WARY', tone: 'amber' },
  { text: 'SEARCHING', tone: 'amber' },
  { text: 'ALARM', tone: 'red' },
];

export class Banners {
  readonly el: HTMLElement;
  private firstAidLeft = 0;
  private aboard = false;
  /** A bomb was armed this frame: judged in `observe`, which sees the world after the tick. */
  private chargeArmed = false;
  /** The signal beep sounded this frame: worded in `observe`, which sees the world after the tick. */
  private signalled = false;
  /** Held charges that had gone up under the column when last looked (10 §2, mission 34). */
  private chargesFired = 0;

  constructor(private readonly def: MissionDef, private readonly bindings?: BindingsPort) {
    this.el = el('div', 'hud-banners');
    this.el.setAttribute('role', 'status');
    this.el.setAttribute('aria-live', 'polite');
  }

  /** Note the ranger facts that change how this frame's events are worded. */
  observe(world: World): void {
    const ranger = world.ranger;
    this.firstAidLeft = ranger.inventory.firstAid;
    this.aboard = ranger.status === 'boarding' || ranger.status === 'extracted';
    if (this.signalled) {
      this.signalled = false;
      const text = signalText(this.def, world);
      if (text !== null) this.push('signal', text, 'amber');
    }
    const fired = world.objective.chargesFired;
    if (fired !== this.chargesFired) {
      const text = fired > this.chargesFired ? chargesFiredText(this.def, fired) : null;
      this.chargesFired = fired;
      if (text !== null) this.push('charges', text, 'green');
    }
    if (this.chargeArmed) {
      this.chargeArmed = false;
      const wasted = uselessChargeText(this.def, world);
      if (wasted !== null) this.push('charge', wasted, 'red');
    }
  }

  handle(events: readonly SimEvent[]): void {
    for (const ev of events) {
      switch (ev.type) {
        case 'alert': {
          const banner = ALERT_BANNERS[clamp(Math.round(ev.level), 0, ALERT_BANNERS.length - 1)];
          if (banner !== undefined) this.push('alert', banner.text, banner.tone, 'GARRISON');
          break;
        }
        case 'osprey':
          if (ev.phase === 'inbound') {
            this.push('osprey', 'OSPREY INBOUND', 'green');
          } else if (ev.phase === 'hovering') {
            this.push('osprey', 'OSPREY ON STATION — REACH THE LADDER', 'green');
          } else if (ev.phase === 'departed' && !this.leftWithRanger(events)) {
            this.push('osprey', 'OSPREY DEPARTED — YOU ARE STRANDED', 'red');
          }
          break;
        case 'reinforcements':
          this.push('reinforcements', `ENEMY REINFORCEMENTS +${ev.count}`, 'red');
          break;
        case 'objective': {
          const progress = progressText(this.def, ev.destroyed, ev.total);
          if (progress !== null) this.push('objective', progress, 'green');
          break;
        }
        case 'objectiveComplete':
          this.push('complete', completeText(this.def), 'green');
          break;
        // A rule the player cannot see is a bug: a lost objective is said out loud, with the reason.
        case 'objectiveFailed':
          this.push('complete', `OBJECTIVE FAILED — ${ev.reason}`, 'red');
          break;
        case 'bombPlaced':
          this.chargeArmed = true;
          break;
        case 'signal':
          this.signalled = true;
          break;
        // The expansion's clocks and switches (10 §3): things that arrive, leave, or go dark are said out loud.
        case 'timedEvent': {
          const said = timedEventText(this.def, ev);
          if (said !== null) this.push('timed', said.text, said.bad ? 'red' : 'amber');
          break;
        }
        case 'blackout':
          this.push('blackout', blackoutText(ev.lights, ev.guns), 'green');
          break;
        case 'pickup':
          // The sim words these itself ("+3 MAGAZINES  +2 GRENADES", "WOUND TREATED"): show verbatim.
          if (isRefusal(ev.what)) this.push('refused', ev.what, 'warn');
          else this.push(null, ev.what, 'amber');
          break;
        case 'rangerDowned': {
          const key = keyHint(this.bindings?.actions() ?? null, 'firstAid', 'F');
          this.push(
            'downed',
            this.firstAidLeft > 0 ? `RANGER DOWN — USE FIRST AID [${key}]` : 'RANGER DOWN — BLEEDING OUT',
            'red',
          );
          break;
        }
        default:
          break;
      }
    }
  }

  clear(): void {
    this.el.replaceChildren();
  }

  /**
   * Slide a banner in under the top edge. A banner replaces any earlier one of the same `kind`;
   * `null` never replaces. The CSS animation runs about 2.6 s and the banner removes itself.
   */
  private push(kind: string | null, text: string, tone: Tone, caption?: string): void {
    const box = this.el;
    if (kind !== null) {
      for (const old of Array.from(box.children)) {
        if (old instanceof HTMLElement && old.dataset['kind'] === kind) old.remove();
      }
    }
    while (box.childElementCount >= MAX_BANNERS) box.firstElementChild?.remove();
    const node = el('div', `hud-banner hud-tone-${tone}`);
    if (kind !== null) node.dataset['kind'] = kind;
    if (caption !== undefined) node.append(el('span', 'hud-banner-caption', caption));
    node.append(text);
    node.addEventListener('animationend', () => node.remove());
    box.append(node);
  }

  /** The Osprey leaving with the ranger on the ladder is not a stranding. */
  private leftWithRanger(events: readonly SimEvent[]): boolean {
    return this.aboard || events.some((ev) => ev.type === 'ended' && ev.outcome === 'extracted');
  }
}
